import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SearchIcon from '@mui/icons-material/Search';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useContext, useEffect, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { EditManagersInfoDto } from '../../../generated-types/editManagersInfoDto';
import { UserDto } from '../../../generated-types/userDto';
import useApi from '../../../lib/useApi';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';

/**
 * Props interface for EditManagersDialog component.
 */
interface EditManagersDialogProps {
  /**
   * Determines if the dialog is open.
   */
  open: boolean;
  /**
   * Function to call when the dialog should be closed.
   */
  onClose: () => void;
}

/**
 * The EditManagersDialog component allows managers to manage the list of managers
 * within their organization. Managers can search and add new managers from the user list,
 * and remove existing managers if needed. The component fetches essential information from
 * the management API and updates the managers upon confirmation.
 */
const EditManagersDialog = (props: EditManagersDialogProps) => {
  // Management API instance
  const managementApi = useApi(ManagementApi);

  // State for managing loading and error
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // State for data fetched from the backend
  const [editManagersInfo, setEditManagersInfo] =
    useState<EditManagersInfoDto | null>(null);

  // State to track the manager updates
  const [managerIds, setManagerIds] = useState<string[]>([]);

  // State for search term and results
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserDto[]>([]);

  // Access contexts
  const snackbar = useContext(SnackbarContext);

  /**
   * Loads the initial data for editing managers, including users, current managers, and the current user's ID.
   */
  const loadData = useCallback(async () => {
    setIsInitialLoading(true);
    setHasError(false);
    setIsSaving(false);

    try {
      const info = await managementApi.GetEditManagersInfo();
      setEditManagersInfo(info);
      setManagerIds(info.managerIds);
      setSearchTerm('');
      setSearchResults([]);
    } catch (error) {
      console.error('Failed to fetch edit managers info', error);
      setHasError(true);
      snackbar.setAlert(
        <Alert variant="filled" severity="error">
          Failed to fetch edit managers info
        </Alert>
      );
      snackbar.triggerSnackbar();
    } finally {
      setIsInitialLoading(false);
    }
  }, [managementApi, snackbar]);

  /**
   * Effect hook to load data when the dialog opens.
   */
  useEffect(() => {
    if (props.open) {
      loadData();
    }
  }, [props.open, loadData]);

  /**
   * Filters organization users based on the search term and excludes current managers and the current user.
   */
  const handleSearch = useCallback(() => {
    if (!editManagersInfo) return;

    if (searchTerm.trim() === '') {
      // Clear search results if search term is empty
      setSearchResults([]);
      return;
    }

    const normalizedSearchTerm = searchTerm.toLowerCase();

    const filteredUsers = editManagersInfo.orgUsers.filter((user) => {
      const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
      const email = user.email.toLowerCase();
      return (
        !managerIds.includes(user.id) && // Exclude current managers
        user.id !== editManagersInfo.managerId && // Exclude current user
        (fullName.includes(normalizedSearchTerm) ||
          email.includes(normalizedSearchTerm))
      );
    });

    setSearchResults(filteredUsers);
  }, [editManagersInfo, managerIds, searchTerm]);

  /**
   * Effect hook to handle the search whenever the searchTerm changes.
   */
  useEffect(() => {
    handleSearch();
  }, [searchTerm, handleSearch]);

  /**
   * Adds a user to the manager list (unless already there).
   */
  const handleAddManager = (userId: string) => {
    if (!managerIds.includes(userId)) {
      setManagerIds([...managerIds, userId]);
    }
  };

  /**
   * Removes a manager from the manager list (unless it's the current user).
   */
  const handleRemoveManager = (userId: string) => {
    if (editManagersInfo && userId !== editManagersInfo.managerId) {
      setManagerIds(managerIds.filter((id) => id !== userId));
    }
  };

  /**
   * Clears the search input and results.
   */
  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
  };

  /**
   * Final submission of the updated manager list.
   */
  const handleSave = async () => {
    // If manager information isn't loaded, do nothing.
    if (!editManagersInfo) return;

    setIsSaving(true);
    try {
      await managementApi.UpdateManagers(managerIds);

      snackbar.setAlert(
        <Alert variant="filled" severity="success">
          Updated managers successfully
        </Alert>
      );
      snackbar.triggerSnackbar();
      props.onClose();
    } catch (error) {
      console.error('Failed to update managers', error);
      snackbar.setAlert(
        <Alert variant="filled" severity="error">
          Failed to update managers
        </Alert>
      );
      snackbar.triggerSnackbar();
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Checks if two arrays are equal
   */
  const arraysEqual = useCallback((a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  }, []);

  /**
   * Determine if changes have occurred
   */
  const hasChanges = editManagersInfo
    ? !arraysEqual(managerIds, editManagersInfo.managerIds)
    : false;

  /** Disable the save button if saving is occurring or no changes have happened. */
  const isSaveDisabled = isSaving || !hasChanges;

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth>
      <DialogTitle>Edit Managers</DialogTitle>
      <DialogContent>
        {hasError ? (
          // Display an error icon and message if there was an error fetching data
          <Box display="flex" flexDirection="column" alignItems="center" my={2}>
            <ErrorOutlineRoundedIcon color="error" style={{ fontSize: 80 }} />
            <Typography variant="h6" mt={2}>
              Failed to load managers information
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              mt={1}
            >
              Please try again or contact support
            </Typography>
          </Box>
        ) : (
          editManagersInfo && (
            <Box>
              {/* Description/instruction */}
              <Typography marginBottom="1em">
                Add or remove a manager from your organization. To add a
                manager, the individual must already be part of your
                organization and have logged in at least once.
              </Typography>

              {/* Current Managers Section */}
              <Box marginBottom="1em">
                <Typography fontWeight="bold">Current Managers</Typography>
                <List disablePadding>
                  {managerIds.map((id) => {
                    // Retrieve user information by ID
                    const user = editManagersInfo.orgUsers.find(
                      (u) => u.id === id
                    );
                    if (!user) return null; // Should not happen
                    const isCurrentUser = id === editManagersInfo.managerId;
                    return (
                      <ListItem key={id}>
                        <ListItemText
                          primary={`${user.firstName} ${user.lastName}`}
                          secondary={user.email}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip
                            title={
                              isCurrentUser
                                ? 'Cannot remove self'
                                : 'Remove Manager'
                            }
                            arrow
                          >
                            <span
                              style={{
                                cursor: isCurrentUser ? 'help' : 'initial',
                              }}
                            >
                              <IconButton
                                edge="end"
                                aria-label="remove"
                                disabled={isCurrentUser}
                                onClick={() => handleRemoveManager(id)}
                                color="error"
                                sx={{
                                  margin: 0,
                                }}
                              >
                                <RemoveCircleOutlineIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                  {managerIds.length === 0 && (
                    <ListItem>
                      <ListItemText primary="No managers found." />
                    </ListItem>
                  )}
                </List>
              </Box>

              {/* Search and Add Managers Section */}
              <Box marginBottom="1em">
                <Typography fontWeight="bold" sx={{ marginBottom: '1em' }}>
                  Add Managers
                </Typography>
                <TextField
                  fullWidth
                  label="Search by name or email"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <Tooltip title="Clear" arrow>
                          <IconButton onClick={clearSearch}>
                            <ClearIcon />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                {searchTerm && (
                  <List
                    disablePadding
                    sx={{ maxHeight: '15em', overflow: 'auto' }}
                  >
                    {searchResults.map((user) => (
                      <ListItem key={user.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleAddManager(user.id)}
                        >
                          <ListItemText
                            primary={`${user.firstName} ${user.lastName}`}
                            secondary={user.email}
                          />
                          <AddCircleOutlineIcon color="success" />
                        </ListItemButton>
                      </ListItem>
                    ))}
                    {searchResults.length === 0 && (
                      <ListItem>
                        <ListItemText primary="No users found." />
                      </ListItem>
                    )}
                  </List>
                )}
              </Box>

              {/* Action Buttons */}
              <Box display="flex" justifyContent="flex-end" marginTop="1em">
                <LoadingButton
                  onClick={props.onClose}
                  color="secondary"
                  variant="outlined"
                  disabled={isSaving}
                  sx={{ marginRight: '1em' }}
                >
                  Cancel
                </LoadingButton>
                <Tooltip title={!hasChanges ? 'No changes to save' : ''} arrow>
                  <span style={{ cursor: hasChanges ? 'initial' : 'help' }}>
                    <LoadingButton
                      onClick={handleSave}
                      variant="contained"
                      disabled={isSaveDisabled}
                      endIcon={<SaveRoundedIcon />}
                    >
                      Save
                    </LoadingButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          )
        )}
        <LoadingCircle isLoading={isInitialLoading} />
      </DialogContent>
    </Dialog>
  );
};

export default EditManagersDialog;
